import axios from "axios";
import { setBlogs, setBlogsError, setBlogsLoading } from "src/store/slices/blogs-slice";
import { store } from "src/store/store";
import { getShopId } from "src/api/axiosInstance";

/**
 * Fetches featured blogs from the CMS API
 * @param {number} page - Page number for pagination (default: 1)
 * @param {number} limit - Number of blogs per page (default: 10)
 * @returns {Promise<Object>} - Object containing blogs array and total count
 */
export const fetchFeaturedBlogs = async (page = 1, limit = 10) => {
  store.dispatch(setBlogsLoading());
  try {
    const shopId = getShopId();
    
    if (!shopId) {
      throw new Error("Shop ID is not available");
    }
    
     const response = await axios.get(
      `https://cms.blinkz.in/items/Blogs?filter[Add_to_Featured_Posts][_eq]=true&filter[status][_eq]=published&filter[Store_Id][_eq]=${shopId}&fields=*,Image.*&page=${page}&limit=${limit}&meta=filter_count&sort=sort_order,date_created`
    );
    
    // Sort by sort_order if available, then by date_created
    const sortedBlogs = response.data?.data ? 
      response.data.data.sort((a, b) => {
        if (a.sort_order && b.sort_order) {
          return a.sort_order - b.sort_order;
        }
        if (a.sort_order && !b.sort_order) return -1;
        if (!a.sort_order && b.sort_order) return 1;
        return new Date(b.date_created) - new Date(a.date_created);
      }) : 
      [];
    
    const result = {
      blogs: sortedBlogs,
      totalCount: response.data?.meta?.filter_count || 0
    };
    
    store.dispatch(setBlogs(sortedBlogs));
    return result;
  } catch (error) {
    console.error("Error fetching featured blogs:", error);
    store.dispatch(setBlogsError(error.message || "Failed to fetch blogs"));
    return { blogs: [], totalCount: 0 };
  }
};

/**
 * Fetches all blogs with pagination
 * @param {number} page - Page number for pagination (default: 1)
 * @param {number} limit - Number of blogs per page (default: 10)
 * @returns {Promise<Object>} - Object containing blogs array and total count
 */
export const fetchAllBlogs = async (page = 1, limit = 10) => {
  try {
    const shopId = getShopId();
    
    if (!shopId) {
      throw new Error("Shop ID is not available");
    }
    
       const response = await axios.get(
      `https://cms.blinkz.in/items/Blogs?filter[status][_eq]=published&filter[Store_Id][_eq]=${shopId}&fields=*,Image.*&page=${page}&limit=${limit}&meta=filter_count&sort=sort_order,date_created`
    );
    
    // Sort by sort_order if available, then by date_created
    const sortedBlogs = response.data?.data ? 
      response.data.data.sort((a, b) => {
        if (a.sort_order && b.sort_order) {
          return a.sort_order - b.sort_order;
        }
        if (a.sort_order && !b.sort_order) return -1;
        if (!a.sort_order && b.sort_order) return 1;
        return new Date(b.date_created) - new Date(a.date_created);
      }) : 
      [];
    
    return {
      blogs: sortedBlogs,
      totalCount: response.data?.meta?.filter_count || 0
    };
  } catch (error) {
    console.error("Error fetching all blogs:", error);
    throw error;
  }
};

/**
 * Fetches a single blog by slug
 * @param {string} slug - The slug of the blog
 * @returns {Promise<Object|null>} - Blog object or null if not found
 */
export const fetchBlogBySlug = async (slug) => {
  try {
    if (!slug) {
      throw new Error("Slug is required");
    }

    const shopId = getShopId();
    
    if (!shopId) {
      throw new Error("Shop ID is not available");
    }

    const response = await axios.get(
      `https://cms.blinkz.in/items/Blogs?filter[slug][_eq]=${slug}&filter[status]=published&filter[Store_Id][_eq]=${shopId}&fields=*,Image.*`
    );
    
    const blog = response.data?.data?.[0] || null;
    
    if (!blog) {
      throw new Error("Blog not found");
    }
    
    return blog;
  } catch (error) {
    console.error("Error fetching blog by slug:", error);
    throw error;
  }
};